// 시세 분석 화면(코인, 나스닥) 공용 부품. 카드 틀과 스타일은 논문 추천의 .paper-* 와 index.css 의 .mkt-* 를 쓴다.
import React, { useEffect, useRef, useState } from 'react';

export const REQUEST_TIMEOUT_MS = 40000;

export const formatKst = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getMonth() + 1}월 ${d.getDate()}일 ${hh}:${mm}`;
};

export const ago = (hours) => (hours < 1 ? `${Math.max(1, Math.round(hours * 60))}분 전` : `${Math.round(hours)}시간 전`);

// 등락 칩. 한국 관례대로 상승은 빨강, 하락은 파랑(색은 index.css 의 --price-up, --price-down).
export const Change = ({ label, value }) => {
  if (value == null) return null;
  const dir = value > 0 ? 'up' : value < 0 ? 'down' : 'flat';
  const sign = value > 0 ? '+' : '';
  return (
    <li className={`mkt-change ${dir}`}>
      <span className="mkt-change-label">{label}</span>
      <span className="mkt-change-value">{sign}{value.toFixed(2)}%</span>
    </li>
  );
};

// "…했다 [1]." 의 번호를 아래 근거로 가는 링크로 바꾼다. HTML 을 주입하지 않는다.
export const WithCitations = ({ text, idPrefix, valid }) => {
  const parts = String(text || '').split(/(\[\d+\])/g);
  return parts.map((part, i) => {
    const m = part.match(/^\[(\d+)\]$/);
    if (!m) return <React.Fragment key={i}>{part}</React.Fragment>;
    const n = Number(m[1]);
    if (!valid.has(n)) return null;
    return (
      <a key={i} className="mkt-cite" href={`#${idPrefix}-a${n}`} aria-label={`근거 ${n}번`}>{n}</a>
    );
  });
};

// 움직임, 원인 분석, 위험 요인 세 칸
export const AnalysisBlocks = ({ item, idPrefix }) => {
  const valid = new Set((item.articles || []).map((a) => a.n));
  return (
    <>
      {item.movement && (
        <section className="mkt-block">
          <h4 className="mkt-block-label">움직임</h4>
          <p>{item.movement}</p>
        </section>
      )}
      {item.drivers && (
        <section className="mkt-block mkt-drivers">
          <h4 className="mkt-block-label">원인 분석</h4>
          <p><WithCitations text={item.drivers} idPrefix={idPrefix} valid={valid} /></p>
        </section>
      )}
      {item.risks && (
        <section className="mkt-block">
          <h4 className="mkt-block-label">위험 요인</h4>
          <p><WithCitations text={item.risks} idPrefix={idPrefix} valid={valid} /></p>
        </section>
      )}
    </>
  );
};

// 근거 목록. 기사와 공식 공시(SEC)를 같은 번호 체계로 보여 준다.
export const EvidenceList = ({ articles, idPrefix }) => {
  if (!articles?.length) return null;
  return (
    <section className="mkt-articles">
      <h4 className="mkt-block-label">근거</h4>
      <ol>
        {articles.map((a) => (
          <li key={a.n} id={`${idPrefix}-a${a.n}`}>
            <span className="mkt-article-n">{a.n}</span>
            <div>
              <a href={a.link} target="_blank" rel="noopener noreferrer">{a.title}</a>
              <span className="mkt-article-meta">
                <span>{a.outlet}</span>
                {a.kind === 'filing'
                  ? <span>{a.date} 제출</span>
                  : a.age_hours != null && <span>{ago(a.age_hours)}</span>}
                {a.kind === 'filing' && <span className="mkt-tag official">공식 공시</span>}
                {a.lang === 'en' && <span className="mkt-tag">영문</span>}
                {a.speculative && <span className="mkt-tag">참고용</span>}
              </span>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
};

export const SkeletonCards = () => (
  <div className="paper-list" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <div key={i} className="paper-card paper-skeleton">
        <div className="paper-rank">{String(i + 1).padStart(2, '0')}</div>
        <div className="paper-body">
          <span className="skeleton-line wide" />
          <span className="skeleton-line" />
          <span className="skeleton-line short" />
        </div>
      </div>
    ))}
  </div>
);

// POST 요청 하나를 취소, 타임아웃, 경쟁 상태까지 다룬다.
export const useAnalysisRequest = (url) => {
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const controllerRef = useRef(null);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const load = async (payload) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const timer = setTimeout(() => controller.abort('timeout'), REQUEST_TIMEOUT_MS);
    setStatus('loading');
    setError('');
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      let body = null;
      try {
        body = await response.json();
      } catch {
        body = null;
      }
      if (!response.ok || !body) {
        throw new Error(body?.message || `서버가 응답하지 않습니다 (HTTP ${response.status}).`);
      }
      if (controllerRef.current !== controller) return;
      setData(body);
      setStatus('done');
    } catch (e) {
      if (controllerRef.current !== controller) return; // 더 새로운 요청이 이미 나갔다
      setStatus('error');
      setError(controller.signal.reason === 'timeout'
        ? '응답이 너무 오래 걸립니다. 잠시 뒤 다시 시도해 주세요.'
        : (e.message || '분석을 불러오지 못했습니다.'));
    } finally {
      clearTimeout(timer);
    }
  };

  return { status, data, error, load };
};
